import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { FaInfoCircle } from "react-icons/fa";

const TOOLTIP_W = 224;
const GAP = 6;
const PAD = 8;

const FieldInfo = ({ text }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const tipRef = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, maxHeight: undefined });

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const place = () => {
      const rect = btnRef.current.getBoundingClientRect();
      const tipH = tipRef.current?.offsetHeight ?? 120;
      let left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
      left = Math.max(PAD, Math.min(left, window.innerWidth - TOOLTIP_W - PAD));
      const spaceBelow = window.innerHeight - rect.bottom - GAP;
      const spaceAbove = rect.top - GAP;
      let top;
      let maxHeight;
      if (spaceBelow >= tipH || spaceBelow >= spaceAbove) {
        top = rect.bottom + GAP;
        maxHeight = Math.max(80, spaceBelow - PAD);
      } else {
        top = Math.max(PAD, rect.top - tipH - GAP);
        maxHeight = Math.max(80, spaceAbove - PAD);
      }
      setStyle({ top, left, maxHeight });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, text]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (wrapRef.current?.contains(e.target) || tipRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const popover =
    open &&
    createPortal(
      <span
        ref={tipRef}
        role="tooltip"
        style={{
          position: "fixed",
          top: style.top,
          left: style.left,
          width: TOOLTIP_W,
          maxHeight: style.maxHeight,
          zIndex: 300,
        }}
        className="overflow-y-auto bg-on-primary-fixed-var text-surface-lowest text-xs rounded-xl px-3 py-2 shadow-lg text-left leading-relaxed"
      >
        {text}
      </span>,
      document.body
    );

  return (
    <span ref={wrapRef} className="relative inline-flex align-middle ml-1">
      <button
        ref={btnRef}
        type="button"
        tabIndex={0}
        aria-label="Field information"
        aria-expanded={open}
        className="text-on-surface-variant hover:text-primary focus:text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-full p-0.5"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <FaInfoCircle size={14} aria-hidden />
      </button>
      {popover}
    </span>
  );
};

export default FieldInfo;
